import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Building2, CheckCircle2, XCircle, RefreshCw, Ban, Play, Search, Clock, Calendar, Filter, KeyRound, LogOut, BarChart3, History, Mail, Camera, Sparkles, Bell, UserCog, ShieldAlert, Copy, ExternalLink, QrCode, Download, AlertTriangle, Database, Monitor, FileText } from "lucide-react";
import { SAInvoiceDialog } from "../components/SAInvoiceDialog";
import { QRCodeCanvas } from "qrcode.react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { SuperAdminAnalytics, SuperAdminAuditLog } from "../components/SuperAdminAnalytics";
import { SuperAdminInvitations } from "../components/SuperAdminInvitations";
import { SuperAdminCameras } from "../components/SuperAdminCameras";
import { SuperAdminAIJaw } from "../components/SuperAdminAIJaw";
import SuperAdminCleanup from "../components/SuperAdminCleanup";
import SuperAdminBackups from "../components/SuperAdminBackups";
import SuperAdminScreenMirror from "../components/SuperAdminScreenMirror";

const SuperAdminPage = () => {
  const { token, logout, impersonate } = useAuth();
  const navigate = useNavigate();
  const [lds, setLds] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending"); // pending / active / all
  const [search, setSearch] = useState("");
  const [subLd, setSubLd] = useState(null);
  const [invoiceLd, setInvoiceLd] = useState(null);
  const [subForm, setSubForm] = useState({ extend_days: 31, subscription_type: "monthly", trial_days: null });
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [pwdSubmitting, setPwdSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("lds"); // lds | analytics | audit
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [saNotifyOptIn, setSaNotifyOptIn] = useState(false);
  const [saEmail, setSaEmail] = useState("");
  const [saEmailDraft, setSaEmailDraft] = useState("");
  const [saMonthlyReport, setSaMonthlyReport] = useState(false);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [impersonateLd, setImpersonateLd] = useState(null);
  const [impersonatePwd, setImpersonatePwd] = useState("");
  const [impersonateSubmitting, setImpersonateSubmitting] = useState(false);
  const [demoLinksOpen, setDemoLinksOpen] = useState(false);
  const [qrData, setQrData] = useState(null); // { label, url }
  const [manualLd, setManualLd] = useState(null); // LD za ročno aktivacijo
  const [manualForm, setManualForm] = useState({
    predsednik_ime: "", predsednik_priimek: "",
    predsednik_lovska_izkaznica: "", predsednik_stevilka_clana: "",
    predsednik_email: "", predsednik_telefon: "",
  });
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualCreds, setManualCreds] = useState(null); // {ld, predsednik} po uspešni aktivaciji

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopirano`);
    } catch (_) {
      toast.error("Kopiranje ni uspelo");
    }
  };

  const handleImpersonate = async () => {
    if (!impersonateLd || !impersonatePwd) { toast.error("Vpišite geslo"); return; }
    setImpersonateSubmitting(true);
    const ok = await impersonate(impersonateLd.id, impersonatePwd);
    setImpersonateSubmitting(false);
    if (ok) {
      setImpersonateLd(null);
      setImpersonatePwd("");
      navigate("/"); // Landing → Dashboard in impersonated LD
    }
  };

  const openManualActivate = (ld) => {
    setManualLd(ld);
    setManualForm({
      predsednik_ime: "", predsednik_priimek: "",
      predsednik_lovska_izkaznica: "", predsednik_stevilka_clana: "",
      predsednik_email: "", predsednik_telefon: "",
    });
    setManualCreds(null);
  };

  const submitManualActivate = async () => {
    if (!manualLd) return;
    const f = manualForm;
    if (!f.predsednik_ime.trim() || !f.predsednik_priimek.trim() ||
        !f.predsednik_lovska_izkaznica.trim() || !f.predsednik_stevilka_clana.trim()) {
      toast.error("Izpolnite ime, priimek, lovsko izkaznico in številko člana.");
      return;
    }
    setManualSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/superadmin/lovske-druzine/${manualLd.id}/activate-manual`,
        f,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`LD "${manualLd.naziv}" aktivirana, predsednik ustvarjen.`);
      setManualCreds(res.data);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Napaka pri aktivaciji");
    } finally {
      setManualSubmitting(false);
    }
  };

  const loadPrefs = async () => {
    setPrefsLoading(true);
    try {
      const res = await axios.get(`${API}/superadmin/preferences`, { headers: { Authorization: `Bearer ${token}` } });
      setSaNotifyOptIn(!!res.data?.sa_notify_opt_in);
      setSaEmail(res.data?.email || "");
      setSaMonthlyReport(!!res.data?.sa_monthly_report_enabled);
    } catch (_) { /* silent */ } finally { setPrefsLoading(false); }
  };

  const savePrefs = async (patch) => {
    setPrefsLoading(true);
    try {
      const res = await axios.put(`${API}/superadmin/preferences`, patch, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.sa_notify_opt_in !== undefined) setSaNotifyOptIn(!!res.data.sa_notify_opt_in);
      if (res.data?.email !== undefined) setSaEmail(res.data.email || "");
      if (res.data?.sa_monthly_report_enabled !== undefined) setSaMonthlyReport(!!res.data.sa_monthly_report_enabled);
      toast.success("Nastavitev shranjena");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri shranjevanju");
    } finally { setPrefsLoading(false); }
  };

  const sendMonthlyReportNow = async () => {
    setPrefsLoading(true);
    try {
      const res = await axios.post(`${API}/superadmin/send-monthly-report-now`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Poročilo poslano na ${res.data?.email} (${res.data?.summary?.sessions} sej)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri pošiljanju");
    } finally { setPrefsLoading(false); }
  };

  const changePassword = async () => {
    if (!pwdForm.current || !pwdForm.next || !pwdForm.confirm) {
      toast.error("Izpolnite vsa polja");
      return;
    }
    if (pwdForm.next !== pwdForm.confirm) {
      toast.error("Novi gesli se ne ujemata");
      return;
    }
    if (pwdForm.next.length < 6) {
      toast.error("Novo geslo mora imeti vsaj 6 znakov");
      return;
    }
    setPwdSubmitting(true);
    try {
      const h = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/superadmin/change-password`, {
        current_stevilka_clana: pwdForm.current,
        new_stevilka_clana: pwdForm.next,
        new_stevilka_clana_confirm: pwdForm.confirm,
      }, { headers: h });
      toast.success("Geslo uspešno spremenjeno. Ponovno se prijavite z novim geslom.");
      setPwdOpen(false);
      setPwdForm({ current: "", next: "", confirm: "" });
      // Force logout so SA logs in with the new password
      setTimeout(() => logout(), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri spremembi gesla");
    } finally {
      setPwdSubmitting(false);
    }
  };

  const applySubscription = async () => {
    if (!subLd) return;
    try {
      const h = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/superadmin/subscription/manual`, {
        ld_id: subLd.id,
        extend_days: subForm.extend_days || null,
        subscription_type: subForm.subscription_type,
        trial_days: subForm.trial_days || null,
      }, { headers: h });
      toast.success("Naročnina posodobljena");
      setSubLd(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Napaka"); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("sl-SI") : "-";
  const daysBetween = (iso) => {
    if (!iso) return 0;
    return Math.round((new Date(iso) - new Date()) / 86400000);
  };

  const fetchAll = async () => {
    try {
      const h = { Authorization: `Bearer ${token}` };
      const [l, s] = await Promise.all([
        axios.get(`${API}/superadmin/lovske-druzine`, { headers: h }),
        axios.get(`${API}/superadmin/stats`, { headers: h }),
      ]);
      setLds(l.data || []);
      setStats(s.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Napaka"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const toggleActive = async (ld) => {
    try {
      const h = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/superadmin/lovske-druzine/${ld.id}`, { aktivna: !ld.aktivna }, { headers: h });
      toast.success(ld.aktivna ? "Deaktivirana" : "Aktivirana");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Napaka"); }
  };

  const handleDedupe = async () => {
    if (!window.confirm("Ali res želite odstraniti podvojene LD zapise? Ta akcija je nepovratna.")) return;
    try {
      const h = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/superadmin/lovske-druzine/dedupe`, {}, { headers: h });
      const d = res.data;
      if (d.removed_total === 0) {
        toast.success("Ni bilo podvojenih zapisov.");
      } else {
        toast.success(`Odstranjenih ${d.removed_total} duplikatov (${d.removed_by_koda.length} po kodi, ${d.removed_by_naziv.length} po nazivu). Ostalo: ${d.remaining} LD-jev.`);
      }
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri deduplikaciji");
    }
  };

  let visible = lds;
  if (filter === "pending") visible = lds.filter(l => l.status === "pending");
  else if (filter === "active") visible = lds.filter(l => l.aktivna);
  else if (filter === "registry") visible = lds.filter(l => l.status === "lzs_registry");
  if (search.length >= 2) visible = visible.filter(l => l.naziv.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="super-admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-primary flex items-center gap-2">
            <Building2 className="h-8 w-8" />Super-admin
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Upravljanje lovskih družin na platformi Dober pogled app</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setPwdOpen(true)} data-testid="sa-change-password" title="Spremeni geslo super-admin računa">
            <KeyRound className="h-4 w-4 mr-1" />
            Spremeni geslo
          </Button>
          <Button variant="outline" onClick={() => { loadPrefs(); setPrefsOpen(true); }} data-testid="sa-notify-prefs-btn" title="Nastavitve prejemanja obvestil">
            <Bell className="h-4 w-4 mr-1" />
            Obvestila
          </Button>
          <Button variant="outline" onClick={() => setDemoLinksOpen(true)} data-testid="sa-demo-links-btn" title="Demo dostopi in deep-linki za deljenje">
            <Play className="h-4 w-4 mr-1" />
            Demo dostopi
          </Button>
          <Button variant="outline" onClick={handleDedupe} data-testid="sa-dedupe" title="Odstrani podvojene LD zapise (po kodi in nazivu)">
            <Filter className="h-4 w-4 mr-1" />
            Odstrani duplikate
          </Button>
          <Button variant="outline" onClick={fetchAll} data-testid="sa-refresh"><RefreshCw className="h-4 w-4 mr-1" />Osveži</Button>
          <Button variant="ghost" onClick={logout} data-testid="sa-logout"><LogOut className="h-4 w-4 mr-1" />Odjava</Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">LZS katalog</p><p className="text-2xl font-bold">{stats.lds_total}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Aktivne</p><p className="text-2xl font-bold text-green-600">{stats.lds_active}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Čakajoče</p><p className="text-2xl font-bold text-amber-600">{stats.lds_pending}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Skupaj članov</p><p className="text-2xl font-bold">{stats.users_total}</p></CardContent></Card>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-1 border-b" data-testid="sa-tabs">
        <button
          onClick={() => setActiveTab("lds")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${activeTab === "lds" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-primary"}`}
          data-testid="sa-tab-lds"
        ><Building2 className="h-4 w-4 inline mr-1" />Lovske družine</button>
        <button
          onClick={() => setActiveTab("analytics")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${activeTab === "analytics" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-primary"}`}
          data-testid="sa-tab-analytics"
        ><BarChart3 className="h-4 w-4 inline mr-1" />Analitika</button>
        <button
          onClick={() => setActiveTab("audit")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${activeTab === "audit" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-primary"}`}
          data-testid="sa-tab-audit"
        ><History className="h-4 w-4 inline mr-1" />Dnevnik</button>
        <button
          onClick={() => setActiveTab("invitations")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${activeTab === "invitations" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-primary"}`}
          data-testid="sa-tab-invitations"
        ><Mail className="h-4 w-4 inline mr-1" />Vabila</button>
        <button
          onClick={() => setActiveTab("cameras")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${activeTab === "cameras" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-primary"}`}
          data-testid="sa-tab-cameras"
        ><Camera className="h-4 w-4 inline mr-1" />Kamere</button>
        <button
          onClick={() => setActiveTab("aijaw")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${activeTab === "aijaw" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-primary"}`}
          data-testid="sa-tab-aijaw"
        ><Sparkles className="h-4 w-4 inline mr-1" />AI ocena</button>
        <button
          onClick={() => setActiveTab("cleanup")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${activeTab === "cleanup" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-primary"}`}
          data-testid="sa-tab-cleanup"
        ><AlertTriangle className="h-4 w-4 inline mr-1" />Čiščenje</button>
        <button
          onClick={() => setActiveTab("backups")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${activeTab === "backups" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-primary"}`}
          data-testid="sa-tab-backups"
        ><Database className="h-4 w-4 inline mr-1" />Backup</button>
        <button
          onClick={() => setActiveTab("mirror")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${activeTab === "mirror" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-primary"}`}
          data-testid="sa-tab-mirror"
        ><Monitor className="h-4 w-4 inline mr-1" />Live view</button>
      </div>

      {activeTab === "analytics" && <SuperAdminAnalytics />}
      {activeTab === "audit" && <SuperAdminAuditLog />}
      {activeTab === "invitations" && <SuperAdminInvitations />}
      {activeTab === "cameras" && <SuperAdminCameras />}
      {activeTab === "aijaw" && <SuperAdminAIJaw />}
      {activeTab === "cleanup" && <SuperAdminCleanup />}
      {activeTab === "backups" && <SuperAdminBackups />}
      {activeTab === "mirror" && <SuperAdminScreenMirror />}

      {activeTab === "lds" && (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lovske družine</CardTitle>
          <CardDescription>Odobri čakajoče registracije ali deaktiviraj obstoječe</CardDescription>
          <div className="flex gap-2 flex-wrap mt-2">
            <Button size="sm" variant={filter === "pending" ? "default" : "outline"} onClick={() => setFilter("pending")}>Čakajoče</Button>
            <Button size="sm" variant={filter === "active" ? "default" : "outline"} onClick={() => setFilter("active")}>Aktivne</Button>
            <Button size="sm" variant={filter === "registry" ? "default" : "outline"} onClick={() => setFilter("registry")}>LZS katalog</Button>
            <Button size="sm" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")}>Vse</Button>
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Poišči LD" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-2">Prikazano {visible.length} LD-jev</p>
          <div className="space-y-2 max-h-[70vh] overflow-y-auto">
            {visible.map(ld => (
              <div key={ld.id} className="flex items-center justify-between gap-3 p-3 border rounded-lg hover:bg-muted/30" data-testid={`ld-row-${ld.koda}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{ld.naziv}</span>
                    {ld.aktivna ? (
                      <Badge className="bg-green-100 text-green-800 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />Aktivna</Badge>
                    ) : ld.status === "pending" ? (
                      <Badge className="bg-amber-100 text-amber-800 text-xs">Čaka odobritev</Badge>
                    ) : ld.status === "lzs_registry" ? (
                      <Badge variant="outline" className="text-xs">Iz LZS kataloga</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800 text-xs"><XCircle className="h-3 w-3 mr-1" />Neaktivna</Badge>
                    )}
                    <Badge variant="outline" className="text-xs">{ld.stevilo_clanov || 0} članov</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    {ld.posta} {ld.kontakt_email ? `• ${ld.kontakt_email}` : ""}
                    {ld.staresina ? ` • starešina: ${ld.staresina}` : ""}
                  </p>
                  {(ld.trial_ends_at || ld.subscription_ends_at) && (
                    <p className="text-xs mt-1">
                      {ld.subscription_ends_at ? (
                        <span className={daysBetween(ld.subscription_ends_at) < 14 ? "text-red-600" : "text-green-700"}>
                          <Calendar className="h-3 w-3 inline mr-1" />
                          Naročnina do {fmt(ld.subscription_ends_at)} ({daysBetween(ld.subscription_ends_at)} dni)
                        </span>
                      ) : ld.trial_ends_at ? (
                        <span className={daysBetween(ld.trial_ends_at) < 7 ? "text-red-600" : "text-amber-700"}>
                          <Clock className="h-3 w-3 inline mr-1" />
                          Preizkus do {fmt(ld.trial_ends_at)} ({daysBetween(ld.trial_ends_at)} dni)
                        </span>
                      ) : null}
                    </p>
                  )}
                </div>
                {ld.status !== "lzs_registry" && (
                  <div className="flex flex-col gap-1">
                    {ld.aktivna && (
                      <Button size="sm" variant="secondary" onClick={() => setImpersonateLd(ld)} data-testid={`impersonate-ld-${ld.koda}`} title="Upravljaj kot predsednik te LD">
                        <UserCog className="h-4 w-4 mr-1" />Upravljaj kot
                      </Button>
                    )}
                    <Button size="sm" variant={ld.aktivna ? "outline" : "default"} onClick={() => toggleActive(ld)} data-testid={`toggle-ld-${ld.koda}`}>
                      {ld.aktivna ? <><Ban className="h-4 w-4 mr-1" />Deaktiviraj</> : <><Play className="h-4 w-4 mr-1" />Aktiviraj</>}
                    </Button>
                    {ld.aktivna && (
                      <Button size="sm" variant="ghost" onClick={() => setSubLd(ld)} data-testid={`sub-ld-${ld.koda}`}>
                        <Calendar className="h-4 w-4 mr-1" />Naročnina
                      </Button>
                    )}
                    {ld.aktivna && (
                      <Button size="sm" variant="ghost" onClick={() => setInvoiceLd(ld)} data-testid={`invoice-ld-${ld.koda}`}>
                        <FileText className="h-4 w-4 mr-1" />Izdaj račun
                      </Button>
                    )}
                  </div>
                )}
                {ld.status === "lzs_registry" && !ld.aktivna && (
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="default" onClick={() => openManualActivate(ld)} data-testid={`manual-activate-${ld.koda}`}
                            title="Ročno aktiviraj z dostopnimi podatki predsednika">
                      <KeyRound className="h-4 w-4 mr-1" />Ročna aktivacija
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      )}

      {/* Manual subscription dialog */}
      {invoiceLd && <SAInvoiceDialog ld={invoiceLd} token={token} onClose={() => setInvoiceLd(null)} />}
      <Dialog open={!!subLd} onOpenChange={(o) => !o && setSubLd(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Naročnina – {subLd?.naziv}</DialogTitle>
            <DialogDescription>Ročno podaljšaj naročnino ali preizkusno obdobje (npr. po bančnem nakazilu).</DialogDescription>
          </DialogHeader>
          {subLd && (
            <div className="space-y-3">
              <div className="text-sm space-y-1 p-3 bg-muted/40 rounded">
                <p>Trenutni preizkus: <strong>{subLd.trial_ends_at ? fmt(subLd.trial_ends_at) : "–"}</strong></p>
                <p>Naročnina: <strong>{subLd.subscription_ends_at ? fmt(subLd.subscription_ends_at) : "–"}</strong></p>
              </div>
              <div className="space-y-2">
                <Label>Podaljšaj naročnino za dni</Label>
                <input type="number" className="w-full border rounded p-2" value={subForm.extend_days || ""}
                  onChange={(e) => setSubForm(f => ({ ...f, extend_days: parseInt(e.target.value) || null }))} />
                <p className="text-xs text-muted-foreground">31 = mesečna, 366 = letna. Podaljša od trenutnega izteka.</p>
              </div>
              <div className="space-y-2">
                <Label>Tip naročnine</Label>
                <select className="w-full border rounded p-2" value={subForm.subscription_type}
                  onChange={(e) => setSubForm(f => ({ ...f, subscription_type: e.target.value }))}>
                  <option value="monthly">Mesečna</option>
                  <option value="yearly">Letna</option>
                </select>
              </div>
              <div className="space-y-2 border-t pt-3">
                <Label>Podaljšaj preizkus za dni (opcijsko)</Label>
                <input type="number" className="w-full border rounded p-2" value={subForm.trial_days || ""}
                  onChange={(e) => setSubForm(f => ({ ...f, trial_days: parseInt(e.target.value) || null }))} placeholder="npr. 30" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubLd(null)}>Prekliči</Button>
            <Button onClick={applySubscription} data-testid="apply-subscription">Shrani</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog open={pwdOpen} onOpenChange={(o) => { if (!o) { setPwdOpen(false); setPwdForm({ current: "", next: "", confirm: "" }); } }}>
        <DialogContent className="max-w-md" data-testid="sa-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Spremeni geslo super-admin računa</DialogTitle>
            <DialogDescription>Vpišite trenutno geslo in izberite novo. Ob shranjevanju vas bomo odjavili – ponovno se prijavite z novim geslom.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="pwd-current">Trenutno geslo</Label>
              <Input id="pwd-current" type="password" autoComplete="current-password" value={pwdForm.current}
                onChange={(e) => setPwdForm(f => ({ ...f, current: e.target.value }))} data-testid="sa-pwd-current" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pwd-new">Novo geslo <span className="text-xs text-muted-foreground">(min. 6 znakov)</span></Label>
              <Input id="pwd-new" type="password" autoComplete="new-password" value={pwdForm.next}
                onChange={(e) => setPwdForm(f => ({ ...f, next: e.target.value }))} data-testid="sa-pwd-new" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pwd-confirm">Potrdi novo geslo</Label>
              <Input id="pwd-confirm" type="password" autoComplete="new-password" value={pwdForm.confirm}
                onChange={(e) => setPwdForm(f => ({ ...f, confirm: e.target.value }))} data-testid="sa-pwd-confirm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdOpen(false)} disabled={pwdSubmitting}>Prekliči</Button>
            <Button onClick={changePassword} disabled={pwdSubmitting} data-testid="sa-pwd-submit">
              {pwdSubmitting ? "Shranjujem…" : "Shrani novo geslo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification preferences dialog */}
      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>        <DialogContent className="max-w-md" data-testid="sa-notify-prefs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bell className="h-5 w-5" /> Nastavitve obvestil</DialogTitle>
            <DialogDescription>
              Kot super-admin privzeto ne prejemate obvestil o dogodkih, koledarju in obvestilih posameznih LD.
              Prejemate samo <b>sistemska obvestila</b> (nove LD registracije, plačilne napake).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 border rounded-lg">
              <input
                type="checkbox"
                id="sa-notify-optin"
                checked={saNotifyOptIn}
                onChange={(e) => savePrefs({ sa_notify_opt_in: e.target.checked })}
                disabled={prefsLoading}
                className="mt-1 h-4 w-4"
                data-testid="sa-notify-optin-toggle"
              />
              <div className="flex-1">
                <Label htmlFor="sa-notify-optin" className="cursor-pointer font-medium">
                  Prejemaj obvestila vseh LD (dogodki, koledar, obvestila)
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Če vklopite, boste dobili push in e-mail obvestila o vsakem novem dogodku, obvestilu in koledarskem
                  vnosu <b>iz vseh LD na platformi</b>. Priporočeno samo za testiranje.
                </p>
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground bg-primary/5 p-2 rounded border border-primary/10">
              <b>Nasvet:</b> tudi če je izklopljeno, boste v Super-admin panelu vedno videli seznam vseh dogodkov v realnem času – samo push/e-mail se ne pošlje.
            </div>

            {/* Mesečno poročilo section */}
            <div className="pt-3 border-t space-y-3">
              <div>
                <Label htmlFor="sa-email" className="font-medium">SA e-mail (za mesečno poročilo)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="sa-email"
                    type="email"
                    placeholder="vase.ime@primer.com"
                    value={saEmailDraft || saEmail}
                    onChange={(e) => setSaEmailDraft(e.target.value)}
                    data-testid="sa-email-input"
                    disabled={prefsLoading}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => savePrefs({ email: (saEmailDraft || saEmail).trim() })}
                    disabled={prefsLoading || (saEmailDraft || saEmail) === saEmail}
                    data-testid="sa-email-save"
                  >Shrani</Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Trenutni: <b>{saEmail || "ni nastavljen"}</b>
                </p>
              </div>
              <div className="flex items-start gap-3 p-3 border rounded-lg">
                <input
                  type="checkbox"
                  id="sa-monthly-report"
                  checked={saMonthlyReport}
                  onChange={(e) => savePrefs({ sa_monthly_report_enabled: e.target.checked })}
                  disabled={prefsLoading || !saEmail}
                  className="mt-1 h-4 w-4"
                  data-testid="sa-monthly-report-toggle"
                />
                <div className="flex-1">
                  <Label htmlFor="sa-monthly-report" className="cursor-pointer font-medium">
                    Mesečno PDF poročilo (1. v mesecu)
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Vsakega 1. v mesecu vam pošljemo PDF povzetek SA sej prejšnjega meseca (upravljanje LD-jev, izvedene akcije, skupno trajanje).
                    {!saEmail && <span className="block text-red-600 mt-1">Najprej vpišite in shranite e-mail.</span>}
                  </p>
                  {saMonthlyReport && saEmail && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={sendMonthlyReportNow}
                      disabled={prefsLoading}
                      className="mt-2 h-7 text-xs"
                      data-testid="sa-monthly-send-now"
                    >
                      Pošlji zdaj (za testiranje)
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrefsOpen(false)}>Zapri</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ročna aktivacija LD - modal */}
      <Dialog open={!!manualLd} onOpenChange={(o) => { if (!o) { setManualLd(null); setManualCreds(null); } }}>
        <DialogContent className="max-w-lg" data-testid="sa-manual-activate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <KeyRound className="h-5 w-5" /> Ročna aktivacija: {manualLd?.naziv}
            </DialogTitle>
            <DialogDescription>
              Vpišite osnovne podatke prvega predsednika. Ustvarjen bo račun in LD aktiviran s 30-dnevnim preizkusom.
              Predsednik lahko podatke dopolni v svojem profilu po prijavi.
            </DialogDescription>
          </DialogHeader>

          {!manualCreds ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Ime *</Label>
                  <Input value={manualForm.predsednik_ime}
                    onChange={(e) => setManualForm({...manualForm, predsednik_ime: e.target.value})}
                    data-testid="manual-ime" />
                </div>
                <div>
                  <Label>Priimek *</Label>
                  <Input value={manualForm.predsednik_priimek}
                    onChange={(e) => setManualForm({...manualForm, predsednik_priimek: e.target.value})}
                    data-testid="manual-priimek" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Lovska izkaznica *</Label>
                  <Input value={manualForm.predsednik_lovska_izkaznica}
                    onChange={(e) => setManualForm({...manualForm, predsednik_lovska_izkaznica: e.target.value})}
                    data-testid="manual-izkaznica" />
                </div>
                <div>
                  <Label>Številka člana * <span className="text-[10px] text-slate-500">(bo geslo)</span></Label>
                  <Input value={manualForm.predsednik_stevilka_clana}
                    onChange={(e) => setManualForm({...manualForm, predsednik_stevilka_clana: e.target.value})}
                    data-testid="manual-stcl" />
                </div>
              </div>
              <div>
                <Label>E-pošta (opcijsko)</Label>
                <Input type="email" placeholder="predsednik@ld-primer.si"
                  value={manualForm.predsednik_email}
                  onChange={(e) => setManualForm({...manualForm, predsednik_email: e.target.value})}
                  data-testid="manual-email" />
                <p className="text-[11px] text-slate-500 mt-0.5">Če vpišete, mu bomo poslali dobrodošlico z dostopnimi podatki.</p>
              </div>
              <div>
                <Label>Telefon (opcijsko)</Label>
                <Input value={manualForm.predsednik_telefon}
                  onChange={(e) => setManualForm({...manualForm, predsednik_telefon: e.target.value})}
                  data-testid="manual-tel" />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                <div className="font-semibold text-emerald-900 flex items-center gap-1 mb-2">
                  <CheckCircle2 className="h-4 w-4" /> Aktivacija uspešna
                </div>
                <div className="text-sm space-y-1">
                  <div><b>LD:</b> {manualCreds.ld.naziv}</div>
                  <div><b>Predsednik:</b> {manualCreds.predsednik.ime} {manualCreds.predsednik.priimek}</div>
                  <div className="pt-2 border-t border-emerald-200">
                    <b>Dostopni podatki (posreduj predsedniku):</b>
                    <div className="mt-1 bg-white rounded p-2 font-mono text-xs">
                      <div className="flex justify-between items-center">
                        <span>Izkaznica: <b>{manualCreds.predsednik.lovska_izkaznica}</b></span>
                        <button className="text-primary hover:underline text-[10px]"
                          onClick={() => copyToClipboard(manualCreds.predsednik.lovska_izkaznica, "Izkaznica")}>
                          <Copy className="h-3 w-3 inline" />
                        </button>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Št. člana (geslo): <b>{manualCreds.predsednik.stevilka_clana}</b></span>
                        <button className="text-primary hover:underline text-[10px]"
                          onClick={() => copyToClipboard(manualCreds.predsednik.stevilka_clana, "Št. člana")}>
                          <Copy className="h-3 w-3 inline" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-xs text-slate-600">
                Predsednik naj se prijavi na aplikacijo s temi podatki in dopolni svoj profil ter doda ostale člane.
              </div>
            </div>
          )}

          <DialogFooter>
            {!manualCreds ? (
              <>
                <Button variant="outline" onClick={() => setManualLd(null)} disabled={manualSubmitting}>Prekliči</Button>
                <Button onClick={submitManualActivate} disabled={manualSubmitting}
                        data-testid="manual-activate-submit"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  {manualSubmitting ? "Aktiviram…" : "Aktiviraj LD"}
                </Button>
              </>
            ) : (
              <Button onClick={() => { setManualLd(null); setManualCreds(null); }}>Zapri</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonate LD confirmation dialog */}
      <Dialog open={!!impersonateLd} onOpenChange={(o) => { if (!o) { setImpersonateLd(null); setImpersonatePwd(""); } }}>
        <DialogContent className="max-w-md" data-testid="sa-impersonate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700"><ShieldAlert className="h-5 w-5" /> Vstop v LD kot predsednik</DialogTitle>
            <DialogDescription>
              Vstopili boste v <b>{impersonateLd?.naziv}</b> kot predsednik. Imeli boste polni dostop
              do vseh modulov te LD (člani, plan odstrela, finance, dogodki, ...).
              <br /><br />
              <b>Vsa dejanja se avdit-logirajo</b> z vašo SA identiteto in oznako impersonacije.
              V zgornjem robu bo rdeč trak z gumbom &quot;Vrni se v Super-admin&quot;.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="imp-pwd">Potrdite z vašim SA geslom</Label>
              <Input
                id="imp-pwd"
                type="password"
                autoComplete="current-password"
                value={impersonatePwd}
                onChange={(e) => setImpersonatePwd(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleImpersonate(); }}
                data-testid="sa-impersonate-pwd"
              />
            </div>
            <div className="text-[11px] text-red-700 bg-red-50 dark:bg-red-950/30 p-2 rounded border border-red-200 dark:border-red-800">
              <b>Občutljive akcije</b> (izbris člana, sprememba gesla) bodo znotraj LD kljub temu zahtevale še eno potrditev.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpersonateLd(null)} disabled={impersonateSubmitting}>Prekliči</Button>
            <Button
              onClick={handleImpersonate}
              disabled={impersonateSubmitting || !impersonatePwd}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="sa-impersonate-confirm"
            >
              {impersonateSubmitting ? "Vstopam…" : "Vstopi kot predsednik"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Demo dostopi – deep-linki + poverilnice */}
      <Dialog open={demoLinksOpen} onOpenChange={setDemoLinksOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sa-demo-links-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Play className="h-5 w-5" /> Demo dostopi</DialogTitle>
            <DialogDescription>
              Deep-linki za marketing, sociale in predstavitve. Podatki v demo LD se avtomatsko resetirajo po 60 min.
            </DialogDescription>
          </DialogHeader>

          <DemoLinksContent copyToClipboard={copyToClipboard} onShowQR={setQrData} />

          <DialogFooter>
            <Button variant="outline" onClick={() => setDemoLinksOpen(false)}>Zapri</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR dialog */}
      <QRDialog qr={qrData} onClose={() => setQrData(null)} />
    </div>
  );
};

export default SuperAdminPage;

// ==================== Demo Links Content Component ====================
const DemoLinkRow = ({ label, path, activeBase, copyToClipboard, onShowQR }) => {
  const full = `${activeBase}${path}`;
  return (
    <div className="flex items-center gap-2 p-2 border rounded hover:bg-muted/40 transition text-xs">
      <span className="flex-shrink-0 w-40 truncate font-medium">{label}</span>
      <code className="flex-1 truncate text-[11px] font-mono text-muted-foreground">{path}</code>
      <button
        onClick={() => copyToClipboard(full, label)}
        className="h-7 px-2 inline-flex items-center gap-1 border rounded hover:bg-primary hover:text-primary-foreground transition"
        data-testid={`copy-demo-${path.replace(/[^a-z0-9]/gi, "-")}`}
        title="Kopiraj polni URL"
      >
        <Copy className="h-3 w-3" />
      </button>
      <button
        onClick={() => onShowQR({ label, url: full })}
        className="h-7 px-2 inline-flex items-center gap-1 border rounded hover:bg-primary hover:text-primary-foreground transition"
        data-testid={`qr-demo-${path.replace(/[^a-z0-9]/gi, "-")}`}
        title="Prikaži QR kodo"
      >
        <QrCode className="h-3 w-3" />
      </button>
      <a
        href={full}
        target="_blank"
        rel="noopener noreferrer"
        className="h-7 px-2 inline-flex items-center gap-1 border rounded hover:bg-primary hover:text-primary-foreground transition"
        title="Odpri v novem zavihku"
      >
        <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
};

const QRDialog = ({ qr, onClose }) => {
  if (!qr) return null;
  const downloadPng = () => {
    const canvas = document.getElementById("demo-qr-canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `demo-qr-${qr.label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  return (
    <Dialog open={!!qr} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="sa-qr-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><QrCode className="h-5 w-5" /> QR koda: {qr.label}</DialogTitle>
          <DialogDescription className="text-xs">
            Skenirajte s telefonom za takojšen vstop v demo. Prenesite PNG za tisk na letak/plakat/stojnico.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2">
          <div className="p-4 bg-white rounded border">
            <QRCodeCanvas
              id="demo-qr-canvas"
              value={qr.url}
              size={240}
              level="H"
              includeMargin={false}
              data-testid="sa-qr-canvas"
            />
          </div>
          <code className="text-[10px] break-all text-center text-muted-foreground max-w-full">{qr.url}</code>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Zapri</Button>
          <Button onClick={downloadPng} data-testid="sa-qr-download">
            <Download className="h-4 w-4 mr-1" />Prenesi PNG
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DemoLinksContent = ({ copyToClipboard, onShowQR }) => {
  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const [customDomain, setCustomDomain] = useState("");
  const activeBase = customDomain.trim() || baseUrl;

  const memberSections = [
    { label: "Karta lovišča", path: "/demo?section=karta" },
    { label: "Plan odstrela", path: "/demo?section=plan" },
    { label: "Koledar", path: "/demo?section=koledar" },
    { label: "Dogodki", path: "/demo?section=dogodki" },
    { label: "Koče", path: "/demo?section=koce" },
    { label: "Sporočila", path: "/demo?section=sporocila" },
    { label: "Obvestila", path: "/demo?section=obvestila" },
    { label: "Člani", path: "/demo?section=clani" },
    { label: "Moje orožje", path: "/demo?section=orozje" },
    { label: "Kamere", path: "/demo?section=kamere" },
    { label: "Ocena starosti parkljarjev", path: "/demo?section=ai" },
    { label: "Datoteke", path: "/demo?section=datoteke" },
    { label: "Moj profil / dolgovi", path: "/demo?section=dolgovi" },
  ];
  const predSections = [
    { label: "Delovne ure", path: "/demo?section=ure" },
    { label: "Statistike", path: "/demo?section=statistike" },
    { label: "SMS", path: "/demo?section=sms" },
    { label: "Dnevnik dogodkov", path: "/demo?section=dnevnik" },
    { label: "Vloge", path: "/demo?section=vloge" },
    { label: "Izvozi", path: "/demo?section=izvozi" },
    { label: "E-mail nastavitve", path: "/demo?section=email-nastavitve" },
    { label: "Arhiv prisotnosti", path: "/demo?section=arhiv-prisotnosti" },
  ];
  const basic = [
    { label: "Dashboard (član)", path: "/demo" },
    { label: "Dashboard (predsednik)", path: "/demo?role=predsednik" },
  ];

  return (
    <div className="space-y-4">
      {/* Credentials */}
      <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded p-3 space-y-1.5">
        <p className="text-xs font-semibold text-amber-900 dark:text-amber-200 uppercase tracking-wide">Ročne poverilnice</p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-2 p-2 bg-background/60 rounded">
            <span className="font-mono flex-1">
              <b>DEMO001</b> / <span className="text-muted-foreground">demo</span>
            </span>
            <span className="text-[10px] text-muted-foreground">Član</span>
            <button
              onClick={() => copyToClipboard("DEMO001 / demo", "Član poverilnice")}
              className="h-6 px-1.5 border rounded hover:bg-primary hover:text-primary-foreground transition"
            ><Copy className="h-3 w-3" /></button>
          </div>
          <div className="flex items-center gap-2 p-2 bg-background/60 rounded">
            <span className="font-mono flex-1">
              <b>DEMOPRED</b> / <span className="text-muted-foreground">demo</span>
            </span>
            <span className="text-[10px] text-muted-foreground">Predsednik</span>
            <button
              onClick={() => copyToClipboard("DEMOPRED / demo", "Predsednik poverilnice")}
              className="h-6 px-1.5 border rounded hover:bg-primary hover:text-primary-foreground transition"
            ><Copy className="h-3 w-3" /></button>
          </div>
        </div>
      </div>

      {/* Base domain override */}
      <div>
        <Label htmlFor="demo-base" className="text-xs">Base URL za kopiranje (opcijsko)</Label>
        <Input
          id="demo-base"
          value={customDomain}
          onChange={(e) => setCustomDomain(e.target.value)}
          placeholder={baseUrl}
          className="h-8 text-xs"
          data-testid="sa-demo-base-url"
        />
        <p className="text-[10px] text-muted-foreground mt-1">
          Trenutno: <code>{activeBase}</code>
          {customDomain === "" && <span> (privzeto trenutna domena)</span>}
        </p>
      </div>

      {/* Osnovni */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Osnovni dostop</h3>
        <div className="space-y-1.5">
          {basic.map(l => <DemoLinkRow key={l.path} {...l} activeBase={activeBase} copyToClipboard={copyToClipboard} onShowQR={onShowQR} />)}
        </div>
      </div>

      {/* Član sekcije */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Sekcije (vloga: član)</h3>
        <div className="space-y-1.5">
          {memberSections.map(l => <DemoLinkRow key={l.path} {...l} activeBase={activeBase} copyToClipboard={copyToClipboard} onShowQR={onShowQR} />)}
        </div>
      </div>

      {/* Predsednik sekcije */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
          Sekcije (vloga: predsednik – auto-promote)
        </h3>
        <div className="space-y-1.5">
          {predSections.map(l => <DemoLinkRow key={l.path} {...l} activeBase={activeBase} copyToClipboard={copyToClipboard} onShowQR={onShowQR} />)}
        </div>
      </div>

      <p className="text-[10px] text-muted-foreground italic">
        Vsakemu linku lahko dodate <code>&amp;role=predsednik</code> ali <code>&amp;role=clan</code> za eksplicitno vlogo.
        Reset threshold: 60 min – v tem oknu se podatki ne resetirajo, kar omogoča stabilne screenshote.
      </p>
    </div>
  );
};